import { useEffect, useMemo, useState, useCallback } from 'react';

import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Lightbox from '../Maps/LightBox';
import Hint from '../Tools/Hint';
import BarChart from '../Campaign/WizardScreens/BarChart/BarChart';
import LinkButton from '../Buttons/LinkButton';
import PercentageGraph from '../Campaign/WizardScreens/PercentageGraph/PercentageGraph';

import {
    formatNumber,
    formatScreenLocation,
    screenLat,
    screenLon,
    getWeeklyImpacts,
    getCostPerSpot,
} from '../../utils/functions';
import { useService } from '../../containers/UseService';
import { useAuth } from '../../containers/UseAuth';
import { newCampaignActions } from '../../store';
import { resolveCurrencyCode } from '../../utils/currency';

const getDefaultCurrencyShortName = () => {
    try {
        return JSON.parse(localStorage.getItem('currencyDefault'))?.short_name;
    } catch {
        return undefined;
    }
};

const mimeFromUrl = (url) => {
    const ext = (url.split('.').pop() || '').toLowerCase().split('?')[0];
    if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
    if (ext === 'png') return 'image/png';
    if (ext === 'webp') return 'image/webp';
    if (ext === 'gif') return 'image/gif';
    return 'image/jpeg';
};

const toInlineUrl = (url) => {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}response-content-disposition=inline&response-content-type=${encodeURIComponent(
        mimeFromUrl(url)
    )}`;
};

const getPublisherName = (row) =>
    row?.publisher && typeof row.publisher === 'object'
        ? row.publisher.name
        : (row?.publisher ?? '-');

const getGalleryImages = (row) => {
    if (!Array.isArray(row?.gallery)) return [];
    return row.gallery.flatMap((g) =>
        Array.isArray(g?.gallery_items)
            ? g.gallery_items.map((it) => it?.image?.url).filter(Boolean)
            : []
    );
};

const Stat = ({ label, value }) => (
    <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {label}
        </div>
        <div className="mt-1 text-base font-semibold text-gray-900 min-h-6">
            {value ?? '-'}
        </div>
    </div>
);

const InfoPill = ({ children }) => (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
        {children}
    </span>
);

const ScreenAudiences = ({ data, screensWithCpm }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const service = useService();
    const newCampaign = useSelector((state) => state.newCampaign);

    const campaignTimezone = newCampaign?.timing?.selectedTimezone?.gmt || 0;
    // Campaign currency first, then the user's default. null when neither is
    // usable, so the request is skipped instead of sending "undefined".
    const selectedCurrencyShortName = resolveCurrencyCode(
        newCampaign?.selectedCurrency?.short_name,
        getDefaultCurrencyShortName()
    );
    const audienceProvider = newCampaign?.demographics?.id;

    const auth = useAuth();
    const userId = auth?.session?.user?.id;
    const isAdmin = auth?.isAdmin();
    const isSupervising = auth?.isSupervising();
    const userMargin = auth?.session?.user?.rtb_margin ?? 0;

    const [screenReports, setScreenReports] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [daySelected, setDaySelected] = useState(null);
    const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '' });

    const galleryImages = useMemo(() => getGalleryImages(data), [data]);

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            if (!data?.external_id || !selectedCurrencyShortName) return;
            setIsLoading(true);
            try {
                const response = await service.getScreensReports(
                    audienceProvider || 'onemata',
                    userId,
                    campaignTimezone,
                    selectedCurrencyShortName,
                    data.external_id
                );
                if (isMounted) setScreenReports(response.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
    }, [
        data?.external_id,
        audienceProvider,
        userId,
        campaignTimezone,
        selectedCurrencyShortName,
        service,
    ]);

    const screenReport = useMemo(
        () => screenReports?.[data?.external_id],
        [screenReports, data?.external_id]
    );
    const currencyKey = selectedCurrencyShortName || 'USD';
    const currencyReport = useMemo(() => {
        if (!screenReport) return null;
        if (screenReport[currencyKey]) return screenReport[currencyKey];
        if (screenReport.USD) return screenReport.USD;
        const firstKey = Object.keys(screenReport)[0];
        return firstKey ? screenReport[firstKey] : null;
    }, [screenReport, currencyKey]);

    const daysOfScreen = useMemo(() => {
        const days = currencyReport?.days;
        return days ? Object.keys(days) : [];
    }, [currencyReport]);

    const days = useMemo(
        () =>
            daysOfScreen.map((d, idx) => ({
                value: idx,
                label: t(`common.days.abbreviated-days.${d}`),
                key: d,
            })),
        [daysOfScreen, t]
    );

    // Initialize selected day
    useEffect(() => {
        if (!daySelected && daysOfScreen.length > 0) {
            setDaySelected({ value: 0, day: daysOfScreen[0] });
        } else if (daySelected && daysOfScreen.length > 0) {
            const safeValue = Math.min(
                daySelected.value,
                daysOfScreen.length - 1
            );
            if (safeValue !== daySelected.value) {
                setDaySelected({
                    value: safeValue,
                    day: daysOfScreen[safeValue],
                });
            }
        }
    }, [daysOfScreen, daySelected]);

    const handleSelect = (val) => {
        setDaySelected({ value: val, day: daysOfScreen[val] });
    };

    const showInMap = (screen) => {
        if (!screen?.geo) return;
        dispatch(
            newCampaignActions.setCurrentPositionMap({
                lat: screen.geo.lat,
                lng: screen.geo.lon,
                zoom: 19,
            })
        );
    };

    const dayData = daySelected?.day
        ? currencyReport?.days?.[daySelected.day]
        : null;
    const normalizedDayData = useMemo(() => {
        if (!dayData) return [];
        if (Array.isArray(dayData)) return dayData;
        if (typeof dayData === 'object') return Object.values(dayData);
        return [];
    }, [dayData]);
    const hasDayData = normalizedDayData.length > 0;

    const weeklyImpacts = getWeeklyImpacts(data);
    const screenCount = Number(data?.screen_count);
    const hasScreenCount = Number.isFinite(screenCount);

    const campaignsUsingScreen = currencyReport?.campaignsUsingScreen;
    const reqSaturationPercentage = currencyReport?.reqSaturationPercentage;

    const costPerSpot = getCostPerSpot(data.deals, selectedCurrencyShortName);
    const spotPrice =
        Number.isFinite(costPerSpot) && costPerSpot !== 0
            ? costPerSpot / (1 - userMargin)
            : null;

    const tierId = screensWithCpm?.find(
        (s) => s.screen_id === data?.external_id
    )?.tierId;

    const showContent = Boolean(
        data && Object.keys(data || {}).length && screensWithCpm
    );
    if (!showContent) return <div />;

    const currentDayLabel =
        daySelected?.value != null ? days?.[daySelected.value]?.label : '';
    const hasDays = days.length > 0;
    const resolvedDayLabel =
        currentDayLabel || days?.[0]?.label || t('common.words.day');

    return (
        <section className="relative mx-auto mt-3 flex flex-col space-y-6 rounded-2xl border bg-white p-5 shadow-sm max-w-[760px]">
            {/* Header */}
            <header className="space-y-2">
                {/* Título */}
                <h2 className="text-[18px] md:text-xl font-bold leading-6 text-gray-900 line-clamp-2">
                    {data?.name ||
                        data?.alias ||
                        data?.label ||
                        data?.external_id}
                </h2>

                <div className="flex flex-wrap items-center gap-2">
                    {data?.venue_type?.name && (
                        <span className="inline-flex items-center rounded-full border bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                            {data.venue_type.name}
                        </span>
                    )}
                    {typeof tierId !== 'undefined' && (
                        <span className="inline-flex items-center rounded-full border bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                            Tier {tierId}
                        </span>
                    )}
                    {hasScreenCount && (
                        <span className="inline-flex items-center rounded-full border bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                            {t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.table-screenCount'
                            )}
                            : {formatNumber(screenCount, 'withcommas')}
                        </span>
                    )}
                    <span className="inline-flex max-w-[260px] items-center rounded-full border bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
                        <span className="shrink-0 text-gray-500">
                            {t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.table-screenProvider'
                            )}
                            :
                        </span>
                        <span className="ml-1 truncate font-semibold">
                            {getPublisherName(data)}
                        </span>
                    </span>
                </div>

                {/* Ubicación */}
                <p className="text-sm text-gray-600 leading-snug flex items-start gap-1">
                    <span className="mt-[2px]">📍</span>
                    <span className="line-clamp-2">
                        {t(
                            'sections.campaigns.new-campaign.wizard-review.blocks.screens.screen-details.location'
                        )}
                        :{' '}
                        <span className="text-gray-800">
                            {formatScreenLocation(data)}
                        </span>
                    </span>
                </p>
            </header>

            {galleryImages.length > 0 && (
                <div className="overflow-hidden rounded-xl border">
                    <Carousel
                        showThumbs={false}
                        showStatus={false}
                        showIndicators={false}
                        dynamicHeight={false}
                        infiniteLoop
                        swipeable
                        emulateTouch
                        onClickItem={(index) => {
                            setLightbox({
                                open: true,
                                images: galleryImages.map((u) =>
                                    toInlineUrl(u)
                                ),
                                index,
                                alt:
                                    data?.alias ||
                                    data?.label ||
                                    data?.external_id,
                            });
                        }}
                    >
                        {galleryImages.map((src, i) => (
                            <div key={i} className="max-h-[360px]">
                                <img
                                    src={toInlineUrl(src)}
                                    referrerPolicy="no-referrer"
                                    alt={
                                        data?.alias ||
                                        data?.label ||
                                        data?.external_id
                                    }
                                    loading="lazy"
                                    style={{
                                        objectFit: 'cover',
                                        maxHeight: 360,
                                    }}
                                />
                            </div>
                        ))}
                    </Carousel>
                </div>
            )}

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3">
                    {data?.slot_duration && (
                        <Stat
                            label={t(
                                'sections.campaigns.new-campaign.wizard-review.blocks.screens.screen-details.slot-duration'
                            )}
                            value={`${data?.slot_duration + 's'}`}
                        />
                    )}
                    <Stat
                        label={t(
                            'sections.campaigns.new-campaign.wizard-screens.screens.table-screenSize'
                        )}
                        value={data?.size}
                    />
                    <Stat
                        label={t(
                            'sections.campaigns.new-campaign.wizard-review.blocks.screens.screen-details.coordinates'
                        )}
                        value={
                            <LinkButton onClick={() => showInMap(data)}>
                                {screenLat(data)}, {screenLon(data)}
                            </LinkButton>
                        }
                    />
                    {isAdmin && (
                        <Stat
                            label={t(
                                'common.components.ReportsTable.spot-cost'
                            )}
                            value={
                                spotPrice !== null
                                    ? formatNumber(
                                          spotPrice,
                                          'properCurrency',
                                          null,
                                          {
                                              currency:
                                                  selectedCurrencyShortName,
                                          }
                                      )
                                    : '-'
                            }
                        />
                    )}
                    {weeklyImpacts != null && (
                        <Stat
                            label={t(
                                'sections.campaigns.new-campaign.wizard-screens.screens.weekly_impacts'
                            )}
                            value={formatNumber(weeklyImpacts, 'rounded')}
                        />
                    )}
                    {(isAdmin || isSupervising) &&
                        typeof campaignsUsingScreen !== 'undefined' && (
                            <Stat
                                label={t(
                                    'sections.campaigns.new-campaign.wizard-screens.sidebar.doughnut.campaigns-using-screen'
                                )}
                                value={campaignsUsingScreen}
                            />
                        )}
                </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.timing.average-impacts'
                            )}
                        </div>
                    </div>

                    <Hint
                        title={t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.average-impacts-title'
                        )}
                        type="info"
                        text={
                            daySelected?.day
                                ? `${t(
                                      'sections.campaigns.new-campaign.wizard-basics.timing.average-impacts-helper'
                                  )}: ${data?.name}`
                                : t(
                                      'sections.campaigns.new-campaign.wizard-basics.timing.average-days-helper'
                                  )
                        }
                        className="mt-1"
                        dataFor="expanded-screen"
                    />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    <InfoPill>
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.timezone'
                        )}{' '}
                        GMT {campaignTimezone}
                    </InfoPill>
                    <InfoPill>
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.audience-provider.title'
                        )}
                        : {audienceProvider || '-'}
                    </InfoPill>
                    {hasDays && (
                        <InfoPill>
                            {t('common.words.day')}: {resolvedDayLabel}
                        </InfoPill>
                    )}
                </div>

                {hasDays ? (
                    <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3">
                        <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>{t('common.words.day')}</span>
                            <span className="font-semibold text-gray-800">
                                {resolvedDayLabel}
                            </span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={days.length - 1}
                            value={daySelected?.value ?? 0}
                            onChange={(e) =>
                                handleSelect(Number(e.target.value))
                            }
                            step={1}
                            className="mt-3 block w-full appearance-none rounded-full bg-gray-200 h-2 accent-gray-700"
                            aria-valuenow={daySelected?.value ?? 0}
                            aria-valuemin={0}
                            aria-valuemax={days.length - 1}
                            aria-label={t('common.words.day')}
                        />
                    </div>
                ) : (
                    <div className="mt-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.average-days-helper'
                        )}
                    </div>
                )}

                {/* Charts */}
                {hasDays && (
                    <div className="mt-5">
                        {isLoading && (
                            <div className="flex items-center gap-2 text-gray-500">
                                <span className="text-sm">
                                    {t('common.words.loading')}…
                                </span>
                            </div>
                        )}

                        {!isLoading && hasDayData && (
                            <>
                                <div className="mt-2 rounded-xl border border-gray-100 bg-white px-3 py-2">
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                        {t('common.words.impacts')}
                                    </div>
                                    <BarChart
                                        data={normalizedDayData}
                                        type="impacts"
                                    />
                                </div>
                                {(isAdmin || isSupervising) && (
                                    <div className="mt-4 rounded-xl border border-gray-100 bg-white px-3 py-2">
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                            {t('common.words.requests')}
                                        </div>
                                        <BarChart
                                            data={normalizedDayData}
                                            type="requests"
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {!isLoading && !hasDayData && (
                            <div className="text-sm text-gray-500">
                                {t(
                                    'common.components.react-data-table.no-data'
                                )}
                            </div>
                        )}
                    </div>
                )}

                {(isAdmin || isSupervising) &&
                    typeof reqSaturationPercentage !== 'undefined' && (
                        <div className="mt-8">
                            <PercentageGraph
                                percentaje={reqSaturationPercentage}
                            />
                        </div>
                    )}
            </div>

            <Lightbox
                open={lightbox.open}
                images={lightbox.images}
                index={lightbox.index ?? 0}
                alt={lightbox.alt}
                onClose={() =>
                    setLightbox({ open: false, images: [], index: 0, alt: '' })
                }
            />
        </section>
    );
};

export default ScreenAudiences;
